import React, { useState, useEffect, useRef } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { styled } from '@mui/material/styles';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-input': {
    textAlign: 'right',
  },
}));

const CurrencyTextField = React.forwardRef((props, ref) => {
  const {
    value,
    defaultValue,
    onChange,
    onBlur,
    currencySymbol = '$',
    decimalCharacter = '.',
    digitGroupSeparator = ',',
    minimumValue = 0,
    outputFormat = 'number',
    ...textFieldProps
  } = props;

  const [displayValue, setDisplayValue] = useState('');
  const [numericValue, setNumericValue] = useState(0);
  const isFocusedRef = useRef(false);

  // Format number to currency display
  const formatCurrency = (num) => {
    if (num === null || num === undefined || num === '') return '';

    const number = parseFloat(num);
    if (isNaN(number)) return '';

    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(number);
  };

  // Parse currency string to number
  const parseCurrency = (str) => {
    if (!str) return 0;

    // Remove currency symbol, spaces, and group separators
    const cleanStr = str
      .replace(new RegExp(`\\${currencySymbol}`, 'g'), '')
      .replace(/\s/g, '')
      .replace(new RegExp(`\\${digitGroupSeparator}`, 'g'), '');

    const number = parseFloat(cleanStr);
    return isNaN(number) ? 0 : number;
  };

  // Sync from props only when not focused — avoids reformatting mid-keystroke
  // (e.g. typing "12" getting stuck as "1.00" after parent re-renders).
  useEffect(() => {
    if (isFocusedRef.current) {
      return;
    }
    const initialValue = value !== undefined ? value : defaultValue;
    if (initialValue !== undefined && initialValue !== null) {
      const numValue = typeof initialValue === 'string' ? parseCurrency(initialValue) : Number(initialValue);
      const safeValue = Number.isFinite(numValue) ? numValue : 0;
      setNumericValue(safeValue);
      setDisplayValue(formatCurrency(safeValue));
    }
  }, [value, defaultValue]);

  const buildSyntheticTarget = (event, nextValue) => ({
    ...event.target,
    id: props.id || event.target?.id,
    name: props.name || event.target?.name,
    value: nextValue,
  });

  const handleInputChange = (event) => {
    const inputValue = event.target.value;

    // Allow only numbers, decimal point, and group separators
    const cleanValue = inputValue.replace(/[^\d.,]/g, '');

    if (cleanValue === '') {
      setDisplayValue('');
      setNumericValue(0);

      if (onChange) {
        const syntheticEvent = {
          ...event,
          target: buildSyntheticTarget(
            event,
            outputFormat === 'number' ? 0 : '0'
          ),
        };
        onChange(syntheticEvent);
      }
      return;
    }

    // Parse the numeric value
    const parsedValue = parseCurrency(cleanValue);

    // Apply minimum value constraint
    const minValue = Number(minimumValue) || 0;
    const finalValue = Math.max(parsedValue, minValue);

    setNumericValue(finalValue);
    setDisplayValue(cleanValue);

    if (onChange) {
      const syntheticEvent = {
        ...event,
        target: buildSyntheticTarget(
          event,
          outputFormat === 'number' ? finalValue : finalValue.toString()
        ),
      };
      onChange(syntheticEvent);
    }
  };

  const handleBlur = (event) => {
    isFocusedRef.current = false;

    // Format the display value on blur
    const formatted = formatCurrency(numericValue);
    setDisplayValue(formatted);

    if (onBlur) {
      const syntheticEvent = {
        ...event,
        target: buildSyntheticTarget(
          event,
          outputFormat === 'number' ? numericValue : numericValue.toString()
        ),
      };
      onBlur(syntheticEvent);
    }
  };

  const handleFocus = (event) => {
    isFocusedRef.current = true;

    // Show raw number on focus for easier editing
    if (numericValue > 0) {
      setDisplayValue(numericValue.toString());
    }

    if (props.onFocus) {
      props.onFocus(event);
    }
  };

  return (
    <StyledTextField
      {...textFieldProps}
      ref={ref}
      value={displayValue}
      onChange={handleInputChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      slotProps={{
        input: {
          startAdornment: currencySymbol ? (
            <InputAdornment position="start">{currencySymbol}</InputAdornment>
          ) : null,
          ...textFieldProps.InputProps,
        }
      }}
    />
  );
});

CurrencyTextField.displayName = 'CurrencyTextField';

export default CurrencyTextField;
